import { createFileRoute } from "@tanstack/react-router";
import { physicianSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { User, GraduationCap } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";
import hero from "@/assets/restore/pratt-hero.webp.asset.json";
import g1 from "@/assets/restore/pratt-g1.jpg.asset.json";
import g2 from "@/assets/restore/pratt-g2.jpg.asset.json";
import g3 from "@/assets/restore/pratt-g3.jpg.asset.json";
import { absoluteUrl } from "@/lib/absolute-url";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/doctors/dr-broc-pratt")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-broc-pratt/" }],
    meta: [
      { title: "Dr. Broc Pratt — Hair Restoration Physician | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Dr. Broc Pratt is a board-certified plastic surgeon based in Charlotte, NC delivering natural, lasting hair restoration results at RESTORE® Hair.",
      },
      { property: "og:title", content: "Dr. Broc Pratt — Hair Restoration Physician" },
      {
        property: "og:description",
        content:
          "Meet Dr. Broc Pratt, board-certified plastic surgeon and RESTORE® Charlotte hair restoration physician.",
      },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianSchema({
            name: "Dr. Broc Pratt",
            jobTitle: "Hair Restoration Physician",
            description: "Dr. Broc Pratt is a board-certified plastic surgeon based in Charlotte, NC delivering natural, lasting hair restoration results at RESTORE® Hair.",
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-broc-pratt/",
            clinic: {
              street: "7725 Ballantyne Commons Parkway, Suite 101",
              city: "Charlotte",
              state: "NC",
            },
          }),
        ),
      },
    ],
  }),
  component: DrPrattPage,
});

const GALLERY = [g1.url, g2.url, g3.url];

function DrPrattPage() {
  const [tab, setTab] = useState<"bio" | "education">("bio");

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pt-10 lg:grid-cols-2 lg:items-end lg:gap-4">
            <div className="pb-10 lg:pb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Dr. Broc Pratt
              </h1>
              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Following a 6 year general surgery residency at Carolinas Medical Center in
                Charlotte, NC, Dr. Pratt successfully completed his fellowship in plastic surgery
                from UNC-Chapel Hill. With a strong foundation in general and plastic surgery,
                he combines technical precision with a keen eye for aesthetic balance—an ideal
                fit for hair restoration.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end lg:self-end">
              <img
                src={hero.url}
                alt="Dr. Broc Pratt — Hair Restoration Physician"
                className="w-full max-w-lg object-contain"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Bio / Education tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-3xl grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("bio")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "bio"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <User size={20} />
                Full Bio
              </button>
              <button
                type="button"
                onClick={() => setTab("education")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "education"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <GraduationCap size={20} />
                Education
              </button>
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              {tab === "bio" ? (
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Broc Pratt's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        After earning his medical degree from the University of Florida, Dr.
                        Pratt trained in general surgery at Carolinas Medical Center, where he
                        also completed a research fellowship in minimally invasive techniques.
                        He then pursued advanced training in plastic and reconstructive
                        surgery at UNC Chapel Hill, one of the nation’s leading programs. In
                        2018, he founded Bespoke Plastic Surgery to provide personalized care
                        rooted in excellence, safety, and artistry.
                      </p>
                      <p>
                        Today, Dr. Pratt is a trusted name in Charlotte’s cosmetic community,
                        helping patients restore their confidence through expert, individualized{" "}
                        <a
                          href="https://restorehair.com/procedure/"
                          className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
                        >
                          hair restoration treatments
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mx-auto max-w-6xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Education &amp; Speciality
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Doctor of Medicine
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        University of Florida
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        College of Medicine
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Residency
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Carolinas Medical Center
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">Charlotte, NC</p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Research Fellowship
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Minimally Invasive Surgery
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Under B. Todd Heniford
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Photo gallery */}
        <section className="bg-background py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-2xl md:text-3xl font-bold text-foreground">
              Learn More About <span className="text-primary">Dr. Pratt</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {GALLERY.map((url, i) => (
                <div key={url} className="aspect-[4/3] w-full overflow-hidden rounded-md">
                  <img
                    src={url}
                    alt={`Dr. Broc Pratt ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
          <SocialPosts />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}